// components/CRM/EtapaWorkflow/CierrePerdidoForm.jsx
import React, { useState } from 'react';
import {
  Box, Typography, TextField, Button, Grid, FormControl,
  InputLabel, Select, MenuItem, Paper, Alert, Checkbox,
  FormControlLabel
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { transicionarEtapa } from '../../../services/crmWorkflowService';

const RAZONES_PERDIDA = [
  { value: 'precio', label: 'Precio (competencia más barata)', reactivable: true },
  { value: 'tiempo', label: 'Tiempo de entrega', reactivable: true },
  { value: 'capacidad', label: 'Capacidad técnica', reactivable: false },
  { value: 'indecision', label: 'Indecisión del cliente / Proyecto postergado', reactivable: true },
  { value: 'relacion', label: 'Relación con competidor', reactivable: false },
  { value: 'financiamiento', label: 'No obtuvo financiamiento', reactivable: true },
  { value: 'especificaciones', label: 'No cumplimos especificaciones', reactivable: false },
  { value: 'otros', label: 'Otros', reactivable: true }
];

const CierrePerdidoForm = ({ oportunidad, onGuardado }) => {
  const [datos, setDatos] = useState({
    razon_principal: '',
    razon_secundaria: '',
    detalle: '',
    competencia_ganadora: '',
    monto_competencia: '',
    lecciones_aprendidas: '',
    reactivable: true,
    fecha_seguimiento: ''
  });
  const [guardando, setGuardando] = useState(false);

  const handleRazonChange = (razon) => {
    const razonObj = RAZONES_PERDIDA.find(r => r.value === razon);
    setDatos({
      ...datos,
      razon_principal: razon,
      reactivable: razonObj ? razonObj.reactivable : true
    });
  };

  const handleGuardar = async () => {
    if (!datos.razon_principal || !datos.detalle) {
      alert('Razón principal y detalle son obligatorios');
      return;
    }

    setGuardando(true);
    try {
      await transicionarEtapa(oportunidad.id, 'cerrado_perdido', {
        razon_principal: datos.razon_principal,
        razon_secundaria: datos.razon_secundaria,
        detalle: datos.detalle,
        competencia_ganadora: datos.competencia_ganadora,
        monto_competencia: datos.monto_competencia ? parseFloat(datos.monto_competencia) : null,
        lecciones_aprendidas: datos.lecciones_aprendidas,
        reactivable: datos.reactivable,
        fecha_seguimiento: datos.fecha_seguimiento || new Date(Date.now() + 180 * 24 * 60 * 60 * 1000)
      }, 'Análisis de oportunidad perdida');
      
      onGuardado?.(datos);
    } catch (error) {
      console.error('Error:', error);
      alert('Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom fontWeight="bold" color="error">
        ❌ Cierre - Oportunidad Perdida
      </Typography>
      
      <Alert severity="warning" sx={{ mb: 3 }}>
        Esta información es crucial para mejorar nuestro proceso de ventas. Sea honesto y detallado.
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth required>
            <InputLabel>Razón principal de pérdida</InputLabel>
            <Select
              value={datos.razon_principal}
              onChange={(e) => handleRazonChange(e.target.value)}
            >
              {RAZONES_PERDIDA.map(r => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label} {r.reactivable ? '(Reactivable)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Razón secundaria (opcional)</InputLabel>
            <Select
              value={datos.razon_secundaria}
              onChange={(e) => setDatos({...datos, razon_secundaria: e.target.value})}
            >
              <MenuItem value="">Ninguna</MenuItem>
              {RAZONES_PERDIDA.map(r => (
                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="Detalle de la pérdida"
            multiline
            rows={4}
            value={datos.detalle}
            onChange={(e) => setDatos({...datos, detalle: e.target.value})}
            placeholder="Explique en detalle por qué se perdió la oportunidad. ¿Qué dijo exactamente el cliente? ¿Qué ofreció la competencia?"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Competencia ganadora"
            value={datos.competencia_ganadora}
            onChange={(e) => setDatos({...datos, competencia_ganadora: e.target.value})}
            placeholder="Nombre de la empresa que ganó"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Monto ofertado por competencia"
            type="number"
            value={datos.monto_competencia}
            onChange={(e) => setDatos({...datos, monto_competencia: e.target.value})}
            InputProps={{ startAdornment: '$' }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Lecciones aprendidas"
            multiline
            rows={3}
            value={datos.lecciones_aprendidas}
            onChange={(e) => setDatos({...datos, lecciones_aprendidas: e.target.value})}
            placeholder="¿Qué podríamos haber hecho diferente? ¿Qué debemos cambiar en nuestro proceso?"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={datos.reactivable}
                onChange={(e) => setDatos({...datos, reactivable: e.target.checked})}
              />
            }
            label="Esta oportunidad es reactivable en el futuro"
          />
        </Grid>

        {datos.reactivable && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Fecha de seguimiento para reactivación"
              type="date"
              value={datos.fecha_seguimiento}
              onChange={(e) => setDatos({...datos, fecha_seguimiento: e.target.value})}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        )}
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="error"
          startIcon={<SaveIcon />}
          onClick={handleGuardar}
          disabled={guardando}
          size="large"
        >
          {guardando ? 'Guardando...' : 'Registrar Pérdida'}
        </Button>
      </Box>
    </Box>
  );
};

export default CierrePerdidoForm;